/**
 * The MiniProductCard component displays the products and helps the user with detailed information about the product.
 *
 * @module views/components/MiniProductCard
 * @memberof -Common
 */
import './MiniProductCard.scss';

import React, { useEffect } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import ProductCardHorizontal from '@ulta/core/components/ProductCardHorizontal/ProductCardHorizontal';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import * as utils from './MiniProductCard';

/**
  * Represents a MiniProductCard component
  *
  * @method
  * @param {MiniProductCardProps} props - React properties passed from composition
  * @returns MiniProductCard
  */
export const MiniProductCard = function( props ){
  const {
    adaRegionLabel = defaultProps.adaRegionLabel,
    componentKey,
    hideRole,
    loading,

    /* actions */
    action,
    actionProps,
    handleAction,
    fullyClickable,
    primaryAction = defaultProps.primaryAction,
    secondaryAction = defaultProps.secondaryAction,

    /* image */
    image,
    imageProps,
    prefetchImage,

    /* text */
    brandName = defaultProps.brandName,
    eyebrow,
    htmlTag,
    productName = defaultProps.productName,

    /* variant */
    variantImage,
    variantLabel = defaultProps.variantLabel,
    variantTypeLabel = defaultProps.variantTypeLabel,

    /* reviews */
    rating,
    reviewAccessibilityLabel,
    reviewCount,

    /* price */
    kitPrice,
    kitPriceAccessibilityLabel,
    listPriceAccessibilityLabel,
    originalPriceLabel,
    price = defaultProps.price,
    salePrice = defaultProps.salePrice,
    salePriceAccessibilityLabel,

    /* inline top messages */
    messages,

    /* bottom tags */
    promoText,
    outOfStock,
    additionalOffersText,
    subscriptionInCart,

    /* DC */
    root,
    rootMargin,
    threshold,
    dataCapture
  } = props;

  /** variables */
  const derivedTagMessage = eyebrow?.message;
  const hasTags = outOfStock || promoText || additionalOffersText || subscriptionInCart;
  const offersInStock = additionalOffersText && !outOfStock;
  const promoInStock = promoText && !outOfStock;

  /** Formatters for incoming tags */
  let formattedTagsArray;

  const [loader, showLoader, hideLoader] = useLoader( {} );

  useEffect( () => {
    !loading && hideLoader();
  }, [loading] );

  if( hasTags ){
    formattedTagsArray = utils.addTags( {
      additionalOffersText,
      offersInStock,
      outOfStock,
      promoInStock,
      promoText,
      subscriptionInCart
    } );
  }

  return (
    <div
      { ...spreadProps( {
        ...( !hideRole ? { // Need to hide role and aria-label if parent has role and aria-label for ADA
          role: 'group',
          'aria-label': adaRegionLabel
        } : {} )
      } ) }
      className='MiniProductCard'
    >
      { hasItems( messages ) &&
        <ul className='MiniProductCard__inlineMessageList'>
          { messages.map(
            ( msgObj, msgIdx ) =>
              msgObj?.message && (
                <li
                  className='MiniProductCard__inlineMessage'
                  key={ `${componentKey}-mini-product-card-${msgIdx}` }
                >
                  <InlineMessage
                    messageType={ utils.getMessageType( msgObj.messageType ) }
                    message={ msgObj.message }
                  />
                </li>
              )
          ) }
        </ul>
      }
      <div className='MiniProductCard__mainContainer'>
        <ProductCardHorizontal
          action={ primaryAction }
          brandName={ brandName }
          fullyClickable={ fullyClickable }
          htmlTag={ htmlTag }
          image={ image }
          imageProps={ imageProps }
          kitPrice={ kitPrice }
          kitPriceAccessibilityLabel={ kitPriceAccessibilityLabel }
          listPrice={ price }
          listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
          originalPriceLabel={ originalPriceLabel }
          prefetchImage={ prefetchImage }
          productName={ productName }
          rating={ rating }
          reviewAccessibilityLabel={ reviewAccessibilityLabel }
          reviewCount={ reviewCount }
          salePrice={ salePrice }
          salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
          style='list'
          tagMessage={ derivedTagMessage }
          tagsBottom={ formattedTagsArray }
          variantImage={ variantImage }
          variantTypeDescription={ variantLabel }
          variantTypeLabel={ variantTypeLabel }
          root={ root }
          rootMargin={ rootMargin }
          threshold={ threshold }
          dataCapture={ dataCapture }
        />
        { secondaryAction &&
          <div className='MiniProductCard__secondaryAction'>
            <Icon
              className='MiniProductCard__icon'
              size='s'
              name={ secondaryAction }
            />
          </div>
        }
      </div>
      { action &&
        <div className='MiniProductCard__buttonActionContainer'>
          <Button
            className='MiniProductCard__buttonAction'
            tertiary
            tiny
            withHover
            ariaHiddenIcon={ true }
            label={ action.label }
            { ...spreadProps( {
              ...actionProps,
              onClick: !action?.navigationType ? () => utils.handleButtonAction( { handleAction, showLoader } ) : undefined,
              action: action?.navigationType ? action : null,
              alignment: action?.navigationType ? 'right' : undefined
            } ) }
          />
        </div>
      }
    </div>
  );
};

/**
 * handleButtonAction function for handling the click of the Button
 * @method
 * @param {Object} methods - Passing setstate methods as an argument
 */
export const handleButtonAction = ( methods ) => {
  const { showLoader, handleAction } = methods;
  showLoader();
  handleAction && handleAction();
};

/**
 * getMessageType function for getting InlineMessage messageType
 * @method
 * @param {Object} methods - Passing setstate methods as an argument
 */
export const getMessageType = ( data ) => {
  return (
    ( data?.color?.includes( '-' ) ? data.color.split( '-' )[0] : data?.color ) ||
    'warning'
  );
};

/**
 * addTags function for creating tags for the card
 * @method
 * @param {Object} data - The data object for creating tags
 */
export const addTags = ( data ) => {
  const {
    additionalOffersText,
    offersInStock,
    outOfStock,
    promoInStock,
    promoText,
    subscriptionInCart
  } = data;

  const tags = [];

  if( subscriptionInCart ){
    tags.push( {
      icon: subscriptionInCart.subsIcon,
      variant: 'icon',
      message: subscriptionInCart.subsLabel
    } );
  }
  if( promoInStock ){
    tags.push( {
      variant: 'value',
      style: 'default',
      message: promoText
    } );
  }
  if( offersInStock ){
    tags.push( {
      variant: 'value',
      style: 'default',
      message: additionalOffersText
    } );
  }
  if( outOfStock ){
    tags.push( {
      variant: 'utilityCompact',
      style: 'error',
      message: outOfStock.message
    } );
  }

  return tags;
};

/**
 * Property type definitions
 *
 * @typedef MiniProductCardProps
 * @type {object}
 * @property {object} action - button action
 * @property {object} actionProps - Button props for button action
 * @property {string} adaRegionLabel - accessibility label for region
 * @property {string} additionalOffersText - This is the additionalOffersText for the product
 * @property {string} brandName - brand name
 * @property {func} handleAction - click handler for `action` Button
 * @property {boolean} hideRole - hides the role for components that have a parent that has a role
 * @property {string} image - product image
 * @property {object} imageProps - Image props for Product image - helps override the default props passed
 * @property {string} listPriceAccessibilityLabel - accessibility label for list price
 * @property {string} messages - inline message object
 * @property {string} originalPriceLabel - accessibility label for original price
 * @property {object} outOfStock = out of stock tag
 * @property {string} price -  price
 * @property {object} primaryAction - link action
 * @property {string} productName - product name
 * @property {string} quantity - quantity
 * @property {string} quantityLabel - quantity label
 * @property {number} rating - rating for review stars
 * @property {string} reviewAccessibilityLabel - accessibility label for review stars
 * @property {number} reviewCount - review count for review stars
 * @property {string} salePriceAccessibilityLabel - accessibility label for sale price
 * @property {string} salePrice - sale price
 * @property {string} secondaryAction - caret forward action
 * @property {object} subscriptionInCart - subscription indicator for cart item
 * @property {string} variantImage - sets the image url for product
 * @property {string} variantLabel - variant label
 * @property {string} variantTypeLabel - variant type
 */
export const propTypes = {
  /** Button action of PC */
  action: PropTypes.shape( {
    graphql: PropTypes.string,
    label: PropTypes.string
  } ),
  /** Additional button props for button action */
  actionProps: PropTypes.object,
  /** Accessibility label for region */
  adaRegionLabel: PropTypes.string,
  /** AdditionalOffersText for the product */
  additionalOffersText: PropTypes.string,
  /** brand name for the product */
  brandName: PropTypes.string.isRequired,
  /** click handler for `action` Button */
  handleAction: PropTypes.func,
  /** hides the role for components that have a parent that has a role */
  hideRole: PropTypes.bool,
  /** product image */
  image: PropTypes.object,
  /** Image props for Product image - helps override the default props passed */
  imageProps: PropTypes.object,
  /** accessibility label for list price */
  listPriceAccessibilityLabel: PropTypes.string,
  /** inline message object */
  messages: PropTypes.arrayOf(
    PropTypes.shape( {
      color: PropTypes.string,
      icon: PropTypes.string,
      message: PropTypes.string
    } )
  ),
  /** accessibility label for original price */
  originalPriceLabel: PropTypes.string,
  /** out of stock tag */
  outOfStock: PropTypes.shape( {
    message: ''
  } ),
  /** price */
  price: PropTypes.string,
  /** link action */
  primaryAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** product name */
  productName: PropTypes.string.isRequired,
  /** quantity */
  quantity: PropTypes.string,
  /** quantity label */
  quantityLabel: PropTypes.string,
  /** rating for review stars */
  rating: PropTypes.number,
  /** accessibility label for review stars */
  reviewAccessibilityLabel: PropTypes.string,
  /** review count for review stars */
  reviewCount: PropTypes.number,
  /** sale price */
  salePrice: PropTypes.string,
  /** accessibility label for sale price */
  salePriceAccessibilityLabel: PropTypes.string,
  /** caret forward action */
  secondaryAction: PropTypes.string,
  /** subscription indicator for cart item */
  subscriptionInCart: PropTypes.shape( {
    subsIcon: PropTypes.string,
    subsLabel: PropTypes.string
  } ),
  /** sets the image url for product */
  variantImage: PropTypes.shape( {
    imageUrl: PropTypes.string
  } ),
  /** variant label */
  variantLabel: PropTypes.string,
  /** variant type */
  variantTypeLabel: PropTypes.string,
  /** root element for intersection observer */
  root: PropTypes.oneOfType( [
    PropTypes.element,
    PropTypes.object
  ] ),
  /** root margin for intersection observer */
  rootMargin: PropTypes.string,
  /** threshold for intersection observer */
  threshold: PropTypes.oneOfType( [
    PropTypes.number,
    PropTypes.arrayOf( PropTypes.number )
  ] ),
  /** data capture object */
  dataCapture: PropTypes.object
};

/**
  * Default values for passed properties
  *
  * @type {object}
  * @property {string} adaRegionLabel='product' - The default adaRegionLabel
  * @property {object} primaryAction=null - The default primaryAction
  * @property {object} subscriptionInCart=null - The default subscriptionInCart
  */
export const defaultProps = {
  adaRegionLabel: 'product',
  primaryAction: null,
  subscriptionInCart: null
};

MiniProductCard.propTypes = propTypes;
MiniProductCard.defaultProps = defaultProps;
export default MiniProductCard;